import React, { useContext, useState } from 'react';

import { RicosContext } from './../../ricos-context';
import { ModalServiceContext } from './../../ricos-context/ModalContext';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { type PmNodeType } from './../../_shared/pm-node-types';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { resolveColorsPalette } from './../../wix-rich-content-common';
import type { ButtonColorType } from './../components/design-component';
import { DEFAULT_PALETTE } from './../defaults';
import type { ButtonPluginEditorConfigCommon } from './../types';
import { NewColorPicker } from './../../ui-components/color-picker/new-color-picker';

interface ButtonColorPickerProps {
  modalId: string;
  nodeId: string;
  nodeType: typeof PmNodeType.LINK_BUTTON | typeof PmNodeType.ACTION_BUTTON;
  colorType: ButtonColorType;
  referenceElement: HTMLDivElement | null;
  config: ButtonPluginEditorConfigCommon;
  defaultBackgroundColor: string | undefined;
  defaultTextColor: string | undefined;
  onColorSelected: (color: string | undefined) => void;
  onColorAdded: (color: string) => void;
}

export const ButtonColorPicker: React.FC<ButtonColorPickerProps> = ({
  modalId,
  nodeId,
  nodeType,
  colorType,
  referenceElement,
  config,
  defaultTextColor,
  defaultBackgroundColor,
  onColorSelected,
  onColorAdded: onColorAddedProp,
}) => {
  const modalService = useContext(ModalServiceContext);
  const { t } = useContext(RicosContext);

  const { pmNodeAttrs } = useNodeAttrs(nodeId, nodeType);

  const logBi = useBiNext({
    modalId,
    pluginId: nodeType === 'LINK_BUTTON' ? 'linkButton' : 'actionButton',
    nodeId,
  });

  const themeColors = resolveColorsPalette(
    DEFAULT_PALETTE.concat(config.colors ? Object.values(config.colors) : []),
    undefined,
    57
  );

  const resolveCustomColors = (args: {
    currentColor: string | undefined;
    getColorFn?: () => string[];
  }) => resolveColorsPalette(args.getColorFn?.() ?? [], args.currentColor, 57);

  const [allCustomColors, setAllCustomColors] = useState(() => ({
    text: resolveCustomColors({
      currentColor: pmNodeAttrs?.styles?.colors?.text ?? defaultTextColor,
      getColorFn: config.getTextColors,
    }),
    border: resolveCustomColors({
      currentColor: pmNodeAttrs?.styles?.colors?.border ?? defaultBackgroundColor,
      getColorFn: config.getBorderColors,
    }),
    background: resolveCustomColors({
      currentColor: pmNodeAttrs?.styles?.colors?.background ?? defaultBackgroundColor,
      getColorFn: config.getBackgroundColors,
    }),
  }));

  const onColorAdded = (color: string) => {
    onColorAddedProp(color);
    setAllCustomColors(prev => ({ ...prev, [colorType]: [...prev[colorType], color] }));
    onColorSelected(color);
  };

  const closeModal = () => modalService.closeModal(modalId);

  const onResetColor = () => onColorSelected(undefined);

  const currentColor = pmNodeAttrs?.styles?.colors?.[colorType]?.toUpperCase();

  const customColors = allCustomColors[colorType];

  return (
    <NewColorPicker
      themeColors={themeColors}
      customColors={customColors}
      currentColor={currentColor}
      title={t('ButtonColourModal_Title')}
      closeModal={closeModal}
      onColorSelected={onColorSelected}
      onColorAdded={onColorAdded}
      onResetColor={onResetColor}
      referenceElement={referenceElement}
      logBi={logBi}
    />
  );
};
